import { useWorkoutCardContext } from './workout-card'
import React, { useMemo } from 'react'
import { useTrainers } from 'src/hook/use-trainers'
import { cn } from 'src/lib/utils'

/**
 * Where the trainer's name comes from.
 *
 * - `lookup` (default) — resolve `workout.trainers[0].id` through `useTrainers()`. This is
 *   what every existing consumer does and is unchanged.
 * - `payload` — read the name already present on `workout.trainers[0].firstName`, and make no
 *   request at all.
 *
 * `payload` exists for the featured shelf on the public login page. `/trainers` is behind
 * `guard.accessToken`, so the default mode would have a signed-out visitor fire an authed
 * request and trip the 401-refresh interceptor. The featured endpoint inlines trainer names
 * precisely so no such lookup is needed.
 */
export type WorkoutCardTrainerSource = 'lookup' | 'payload'

interface WorkoutCardTrainerProps {
  className?: string
  source?: WorkoutCardTrainerSource
}

export const WorkoutCardTrainer = ({ className, source = 'lookup' }: WorkoutCardTrainerProps): React.ReactElement => {
  const { workout } = useWorkoutCardContext()
  // Called unconditionally — hooks must be — but switched off in payload mode so no request
  // leaves the page.
  const { data: trainers } = useTrainers({ enabled: source === 'lookup' })

  const trainerName = useMemo(() => {
    const [firstTrainer] = workout.trainers ?? []
    if (!firstTrainer) return undefined
    if (source === 'payload') return firstTrainer.firstName
    return trainers?.find((t) => t.id === firstTrainer.id)?.firstName
  }, [source, trainers, workout.trainers])

  if (!trainerName) {
    return <></>
  }

  return <p className={cn('font-inter text-xl font-bold text-ivory flex-1 shrink mr-2', className)}>{trainerName}</p>
}
